import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Upload,
  Button,
  Table,
  Alert,
  Typography,
  Space,
  Select,
  Steps,
  message,
  Modal,
  Input,
  Form,
  Tag,
  Divider,
  Row,
  Col,
  Statistic,
  Tooltip,
  Checkbox,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  InfoCircleOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import * as XLSX from 'xlsx';
import { TournamentService } from '@/services/tournament.service';
import { TournamentCategory } from '@/types/api.types';
import { logger } from '@/utils/logger';
import apiService from '@/services/api.service';
import type { AxiosResponse } from 'axios';

const { Title, Paragraph } = Typography;
const { Step } = Steps;

interface ParticipantData {
  [key: string]: any;
  _rowIndex?: number;
  _errors?: string[];
  _valid?: boolean;
}

interface FieldMapping {
  [fieldName: string]: string | null; // fieldName -> column index/name
}

interface ImportError {
  row: number;
  error: string;
  data: any;
}

interface ImportResult {
  success: number;
  failed: number;
  errors: ImportError[];
  created: any[];
  suggestions?: Array<{
    row: number;
    originalCategory: string;
    suggestedCategory: { id: number; name: string };
  }>;
}

const REQUIRED_FIELDS = ['firstName', 'lastName'];
const OPTIONAL_FIELDS = [
  'middleName',
  'dateOfBirth',
  'gender',
  'weight',
  'beltLevel',
  'categoryName',
  'federationName',
  'clubName',
  'region',
  'coachFullName',
];

const FIELD_LABELS: Record<string, string> = {
  firstName: 'Имя',
  lastName: 'Фамилия',
  middleName: 'Отчество',
  dateOfBirth: 'Дата рождения',
  gender: 'Пол',
  weight: 'Вес',
  beltLevel: 'Уровень пояса',
  categoryName: 'Категория',
  federationName: 'Название федерации',
  clubName: 'Название клуба',
  region: 'Регион',
  coachFullName: 'Тренер (ФИО)',
};

const ZERO_WIDTH_RE = /[\u200B-\u200D\uFEFF]/g;
const cleanCellText = (cell: unknown): string =>
  String(cell ?? '').replace(ZERO_WIDTH_RE, '').trim();

type HeaderRowResult = { rowIndex: number; headerRow: any[] };

// Универсальная функция поиска строки заголовков.
// Важно: НЕ "сжимаем" колонки (A/B/C...), чтобы не терять смещение (например, старт с B18).
const findHeaderRow = (data: any[][], maxRows: number = 200): HeaderRowResult => {
  const lastNameKeys = ['фамилия', 'lastname', 'last name', 'фамил'];
  const firstNameKeys = ['имя', 'firstname', 'first name'];

  const hasAny = (text: string, keys: string[]) => keys.some(k => text.includes(k));

  for (let i = 0; i < Math.min(maxRows, data.length); i++) {
    const row = data[i];
    if (!row || !Array.isArray(row) || row.length === 0) continue;

    const nonEmpty = row
      .map((cell, colIndex) => ({ colIndex, text: cleanCellText(cell) }))
      .filter(x => x.text.length > 0);

    if (nonEmpty.length < 2) continue;

    const rowText = nonEmpty.map(x => x.text.toLowerCase()).join(' ');
    const hasLast = hasAny(rowText, lastNameKeys);
    const hasFirst = hasAny(rowText, firstNameKeys);

    // Строка заголовков должна содержать как минимум "Имя" и "Фамилия"
    if (hasLast && hasFirst) {
      return { rowIndex: i, headerRow: row };
    }
  }

  return { rowIndex: -1, headerRow: [] };
};

// Поиск названия клуба по всему документу (не в столбце: шапка, подпись и т.д.)
const findClubNameInDocument = (dataRows: any[][], maxRows: number = 50, maxCols: number = 30): string | undefined => {
  const labelRegex = /^(клуб|организация|школа|название\s+клуба|club|organization|school)\s*[:：]?\s*$/i;
  const inlineRegex = /^(клуб|организация|школа|название\s+клуба|club|organization|school)\s*[:：]?\s*(.+)$/i;
  // Не подставлять как клуб значения, которые являются заголовками других колонок
  const isInvalidClubName = (name: string) => !name || /^категори/i.test(name.trim());
  for (let r = 0; r < Math.min(maxRows, dataRows.length); r++) {
    const row = dataRows[r];
    if (!row || !Array.isArray(row)) continue;
    for (let c = 0; c < Math.min(maxCols, row.length); c++) {
      const text = cleanCellText(row[c]);
      if (!text) continue;
      const inlineMatch = text.match(inlineRegex);
      if (inlineMatch) {
        const name = cleanCellText(inlineMatch[2]);
        if (name.length > 0 && !isInvalidClubName(name)) return name;
      }
      if (labelRegex.test(text)) {
        const nextCell = row[c + 1] != null ? cleanCellText(row[c + 1]) : '';
        if (nextCell.length > 0 && !isInvalidClubName(nextCell)) return nextCell;
      }
    }
  }
  return undefined;
};

interface ParticipantImportProps {
  tournamentId: number;
}

export const ParticipantImport: React.FC<ParticipantImportProps> = ({ tournamentId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [rawData, setRawData] = useState<any[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fieldMapping, setFieldMapping] = useState<FieldMapping>({});
  const [parsedData, setParsedData] = useState<ParticipantData[]>([]);
  const [validationErrors, setValidationErrors] = useState<Map<number, string[]>>(new Map());
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editForm] = Form.useForm();
  const [categories, setCategories] = useState<TournamentCategory[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [autoMapEnabled, setAutoMapEnabled] = useState(true);
  const [serverValidating, setServerValidating] = useState(false);
  const [serverValidationDone, setServerValidationDone] = useState(false);
  const [filteringExisting, setFilteringExisting] = useState(false);
  const [excludedExistingCount, setExcludedExistingCount] = useState(0);

  // Ключ для сопоставления участника (фамилия + имя, нормализованные)
  const participantKey = (firstName: string | undefined, lastName: string | undefined): string => {
    const f = (firstName ?? '').toString().trim().toLowerCase();
    const l = (lastName ?? '').toString().trim().toLowerCase();
    return `${l}|${f}`;
  };

  // Загрузить всех зарегистрированных участников турнира (все страницы)
  const fetchAllRegisteredParticipantKeys = useCallback(async (tid: number): Promise<Set<string>> => {
    const keys = new Set<string>();
    let page = 1;
    const limit = 1000;
    let hasMore = true;
    while (hasMore) {
      const res = await apiService.get<{
        data?: any[];
        pagination?: { total: number; hasMore?: boolean };
      }>(`/tournaments/${tid}/registered-participants`, { params: { page, limit } });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      list.forEach((p: any) => {
        const part = p.participant ?? p;
        const firstName = part.firstName ?? part.first_name ?? p.firstName;
        const lastName = part.lastName ?? part.last_name ?? p.lastName;
        if (firstName || lastName) keys.add(participantKey(firstName, lastName));
      });
      const pagination = res.data?.pagination;
      if (pagination?.hasMore === false) hasMore = false;
      else if (list.length < limit) hasMore = false;
      else page += 1;
    }
    return keys;
  }, []);

  // Загрузка категорий турнира
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await TournamentService.getTournamentCategories(tournamentId);
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } catch (error) {
        logger.error('Error fetching categories:', error);
        message.error('Ошибка при загрузке категорий турнира');
      }
    };

    fetchCategories();
  }, [tournamentId]);

  // Автоматический маппинг полей
  const autoMapFields = useCallback((fileHeaders: string[]): FieldMapping => {
    const mapping: FieldMapping = {};

    fileHeaders.forEach((header) => {
      // header хранится в виде: "<colIndex>__<COL>: <Header>"
      const headerLabelPart = header.includes('__') ? header.split('__').slice(1).join('__') : header;
      const headerLabel = headerLabelPart.replace(/^[A-Z]{1,3}:\s*/i, '').trim();
      const headerLower = headerLabel.toLowerCase().trim();

      // Маппинг по ключевым словам
      if (headerLower.includes('фамилия') || headerLower.includes('lastname') || headerLower === 'last name') {
        mapping['lastName'] = header;
      } else if (headerLower.includes('имя') || headerLower.includes('firstname') || headerLower === 'first name') {
        mapping['firstName'] = header;
      } else if (headerLower.includes('отчество') || headerLower.includes('middlename') || headerLower === 'middle name') {
        mapping['middleName'] = header;
      } else if (headerLower.includes('дата') || headerLower.includes('birth') || headerLower.includes('рождени') || headerLower.includes('др')) {
        mapping['dateOfBirth'] = header;
      } else if (headerLower.includes('пол') || headerLower.includes('gender') || headerLower === 'sex') {
        mapping['gender'] = header;
      } else if (headerLower.includes('вес') || headerLower.includes('weight')) {
        mapping['weight'] = header;
      } else if (headerLower.includes('пояс') || headerLower.includes('belt') || headerLower.includes('level')) {
        mapping['beltLevel'] = header;
      } else if (headerLower.includes('категори') || headerLower.includes('category') || headerLower.includes('cat')) {
        mapping['categoryName'] = header;
      } else if (headerLower.includes('федерац') || headerLower.includes('federation')) {
        mapping['federationName'] = header;
      } else if ((headerLower.includes('клуб') || headerLower.includes('club')) && !headerLower.includes('категори')) {
        mapping['clubName'] = header;
      } else if (headerLower.includes('регион') || headerLower.includes('region')) {
        mapping['region'] = header;
      } else if (headerLower.includes('тренер') || headerLower.includes('coach') || headerLower.includes('trainer')) {
        mapping['coachFullName'] = header;
      }
    });

    return mapping;
  }, []);

  // Парсинг файла
  const parseFile = async (file: File) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null, raw: false }) as any[][];

      if (jsonData.length < 2) {
        throw new Error('Файл пуст или содержит недостаточно данных');
      }

      // Находим строку с заголовками универсальным способом
      const { rowIndex, headerRow } = findHeaderRow(jsonData);

      if (rowIndex < 0 || !headerRow || headerRow.length === 0) {
        throw new Error('Не удалось найти строку заголовков (проверьте, что есть колонки "Имя" и "Фамилия")');
      }

      // Важно: сохраняем РЕАЛЬНЫЕ индексы колонок (A/B/C...), не сжимаем пустые слева.
      const fileHeaders = headerRow
        .map((cell, colIndex) => {
          const label = cleanCellText(cell);
          if (!label) return null;
          const colLetter = XLSX.utils.encode_col(colIndex);
          return `${colIndex}__${colLetter}: ${label}`;
        })
        .filter(Boolean) as string[];

      if (fileHeaders.length === 0) {
        throw new Error('Строка заголовков найдена, но все ячейки пустые');
      }

      setHeaders(fileHeaders);
      setRawData(jsonData);

      // Автоматический маппинг
      if (autoMapEnabled) {
        const autoMapping = autoMapFields(fileHeaders);
        setFieldMapping(autoMapping);
      }

      setCurrentStep(1);
      message.success('Файл успешно загружен');
    } catch (error) {
      logger.error('Error parsing file:', error);
      message.error('Ошибка при парсинге файла: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
    }
  };

  // Парсинг данных с учетом маппинга
  const parseDataWithMapping = useCallback(() => {
    if (!headers.length || !rawData.length) return;

    const data: ParticipantData[] = [];
    // Находим индекс строки с заголовками
    const { rowIndex: headerRowIndex } = findHeaderRow(rawData);
    if (headerRowIndex < 0) {
      // Если по каким-то причинам не нашли заголовки при повторном поиске,
      // просто не парсим, чтобы не получить мусор.
      setParsedData([]);
      return;
    }
    const dataStartIndex = headerRowIndex + 1; // Начинаем со следующей строки после заголовков

    // Клуб ищем по документу (не в столбце): сканируем область над таблицей
    const clubNameFromDocument = findClubNameInDocument(rawData, headerRowIndex + 1);

    // Категории турнира: для строк «категория в ячейке, под ней участники».
    // ВАЖНО: используем только если в маппинге НЕТ отдельной колонки categoryName.
    // Если колонка категории замаплена, считаем, что категория задаётся построчно,
    // а не через отдельные "строки категории".
    const hasCategoryColumn = !!fieldMapping.categoryName;
    const categoryNameSet = hasCategoryColumn
      ? new Set<string>()
      : new Set(categories.map(c => c.name.trim().toLowerCase()));
    let currentCategoryName: string | undefined;

    for (let i = dataStartIndex; i < rawData.length; i++) {
      const row = rawData[i];
      if (!row || !Array.isArray(row)) continue;

      // Проверяем, не является ли строка «строкой категории» (название категории в ячейке, под ней участники)
      // Только если НЕТ отдельной колонки категории (hasCategoryColumn === false)
      let isCategoryRow = false;
      if (categoryNameSet.size > 0) {
        for (let c = 0; c < row.length; c++) {
          const cellText = cleanCellText(row[c]);
          if (!cellText) continue;
          const key = cellText.trim().toLowerCase();
          if (categoryNameSet.has(key)) {
            const cat = categories.find(cat => cat.name.trim().toLowerCase() === key);
            if (cat) currentCategoryName = cat.name;
            isCategoryRow = true;
            break;
          }
          const afterColon = cellText.replace(/^[^:：]*[:：]\s*/, '').trim();
          if (afterColon && categoryNameSet.has(afterColon.toLowerCase().trim())) {
            const cat = categories.find(cat => cat.name.trim().toLowerCase() === afterColon.toLowerCase().trim());
            if (cat) currentCategoryName = cat.name;
            isCategoryRow = true;
            break;
          }
        }
      }
      if (isCategoryRow) continue;

      const hasAny = row.some((cell: unknown) => cleanCellText(cell).length > 0);
      if (!hasAny) continue;

      const participant: ParticipantData = { _rowIndex: i + 1 };

      // Маппим поля
      Object.entries(fieldMapping).forEach(([fieldName, columnName]) => {
        if (columnName) {
          // columnName хранится в виде: "<colIndex>__<COL>: <Header>"
          const colIndexStr = columnName.split('__')[0];
          const colIndex = Number(colIndexStr);
          if (Number.isFinite(colIndex) && row[colIndex] !== undefined && row[colIndex] !== null) {
            participant[fieldName] = row[colIndex]?.toString().trim() || '';
          }
        }
      });

      // Клуб подставляем из документа, если нашли, НО только если в строке
      // ещё нет значения из соответствующей колонки. Не подставлять "Категория" и пустые.
      const clubValue = (participant.clubName && String(participant.clubName).trim()) || '';
      const isCategoryHeader = /^категори/i.test(clubValue);
      if (clubValue && isCategoryHeader) {
        participant.clubName = undefined;
      } else if (clubNameFromDocument && !participant.clubName) {
        participant.clubName = clubNameFromDocument;
      }

      // Категорию подставляем из «строки категории» над участниками; иначе из колонки
      if (currentCategoryName) {
        participant.categoryName = currentCategoryName;
      }

      // Пропускаем пустые строки
      if (!participant.firstName && !participant.lastName) {
        continue;
      }

      // Убеждаемся, что у каждого участника есть уникальный ключ
      if (!participant._rowIndex) {
        participant._rowIndex = i + 1;
      }

      data.push(participant);
    }

    // Оставляем только тех, кого ещё нет в турнире
    setFilteringExisting(true);
    setExcludedExistingCount(0);
    fetchAllRegisteredParticipantKeys(tournamentId)
      .then((existingKeys) => {
        const filtered = data.filter(
          (row) => !existingKeys.has(participantKey(row.firstName as string, row.lastName as string))
        );
        setParsedData(filtered);
        validateData(filtered);
        setExcludedExistingCount(data.length - filtered.length);
      })
      .catch((err) => {
        logger.error('Error filtering existing participants', err);
        message.warning('Не удалось загрузить список участников турнира. Показаны все строки из файла.');
        setParsedData(data);
        validateData(data);
      })
      .finally(() => setFilteringExisting(false));
  }, [headers, rawData, fieldMapping, categories, tournamentId, fetchAllRegisteredParticipantKeys]);

  // Валидация данных
  const validateData = (data: ParticipantData[]) => {
    const errors = new Map<number, string[]>();

    data.forEach((participant, index) => {
      const rowErrors: string[] = [];

      // Проверка обязательных полей
      if (!participant.firstName || participant.firstName.trim().length === 0) {
        rowErrors.push('Имя обязательно');
      }
      if (!participant.lastName || participant.lastName.trim().length === 0) {
        rowErrors.push('Фамилия обязательна');
      }


      // Проверка веса
      if (participant.weight) {
        const rawWeight = participant.weight.toString().trim().replace(',', '.');
        const weight = parseFloat(rawWeight);
        // Допускаем более лёгких участников: 10–200 кг
        if (isNaN(weight) || weight < 10 || weight > 200) {
          rowErrors.push('Вес должен быть числом от 10 до 200 кг');
        }
      }

      // Проверка даты рождения
      if (participant.dateOfBirth) {
        const dateStr = participant.dateOfBirth.toString().trim();
        // Используем ту же функцию, что и при отправке в API
        const parsed = parseDate(dateStr);
        if (!parsed) {
          rowErrors.push('Неверный формат даты (используйте ДД.ММ.ГГГГ)');
        }
      }

      // Проверка пола
      if (participant.gender) {
        const gender = participant.gender.toString().toLowerCase();
        if (gender !== 'male' && gender !== 'female' && gender !== 'м' && gender !== 'ж' && 
            gender !== 'мужской' && gender !== 'женский') {
          rowErrors.push('Пол должен быть: male/female или м/ж');
        }
      }

      // Категорию по названию не проверяем на «есть в турнире»: при импорте бэкенд сам создаст
      // категорию в системе и привяжет к турниру, если такой ещё нет (см. bulk-upload).

      const key = participant._rowIndex ?? index + 1;

      if (rowErrors.length > 0) {
        errors.set(key, rowErrors);
        participant._errors = rowErrors;
        participant._valid = false;
      } else {
        participant._valid = true;
        participant._errors = [];
      }
    });

    setValidationErrors(errors);
  };

  // Дополнительная серверная валидация (проверка конфликтов с уже существующими участниками/клубами)
  const runServerValidation = useCallback(async () => {
    if (!parsedData.length) return;

    try {
      setServerValidating(true);

      // Приводим данные к тому же формату, что и при импорте,
      // но добавляем rowIndex, чтобы сервер вернул номера строк,
      // совпадающие с _rowIndex (строки Excel).
      const participantsToValidate = parsedData.map((p, index) => ({
        rowIndex: p._rowIndex ?? index + 1,
        firstName: p.firstName,
        lastName: p.lastName,
        middleName: p.middleName || undefined,
        dateOfBirth: p.dateOfBirth ? parseDate(p.dateOfBirth.toString()) : undefined,
        gender: normalizeGender(p.gender?.toString()),
        weight: p.weight ? parseFloat(p.weight.toString()) : undefined,
        beltLevel: p.beltLevel || undefined,
        categoryName: p.categoryName?.toString().trim() || undefined,
        federationName: p.federationName?.toString().trim() || undefined,
        clubName: (() => {
          const v = p.clubName?.toString().trim();
          return v && !/^категори/i.test(v) ? v : undefined;
        })(),
        region: p.region?.toString().trim() || undefined,
        coachFullName: p.coachFullName?.toString().trim() || undefined,
      }));

      const response = await apiService.post<{
        success: boolean;
        data: {
          valid: number;
          invalid: number;
          errors: Array<{ row: number; errors: string[]; data: any }>;
          canProceed: boolean;
        };
      }>('/participants/bulk-upload/validate', {
        participants: participantsToValidate,
        tournamentId,
      });

      if (!response.data.success) {
        return;
      }

      const serverErrors = response.data.data.errors || [];
      if (!serverErrors.length) {
        return;
      }

      // Мержим серверные ошибки в существующую карту validationErrors
      const newErrors = new Map<number, string[]>(validationErrors);
      const updatedData = [...parsedData];

      serverErrors.forEach(err => {
        const rowKey = err.row;
        const existing = newErrors.get(rowKey) || [];
        const merged = [...existing, ...err.errors];
        newErrors.set(rowKey, merged);

        // Обновляем _errors и _valid у соответствующей строки
        const idx = updatedData.findIndex(p => p._rowIndex === rowKey);
        if (idx !== -1) {
          updatedData[idx] = {
            ...updatedData[idx],
            _errors: merged,
            _valid: false,
          };
        }
      });

      setValidationErrors(newErrors);
      setParsedData(updatedData);
    } catch (error) {
      logger.error('Server validation failed:', error);
      message.error('Ошибка при проверке данных по базе');
    } finally {
      setServerValidating(false);
      setServerValidationDone(true);
    }
  }, [parsedData, tournamentId, validationErrors]);

  // Обновление маппинга
  useEffect(() => {
    if (currentStep >= 1 && headers.length > 0) {
      parseDataWithMapping();
      // При повторном парсинге данных считаем, что серверную валидацию нужно выполнить заново
      setServerValidationDone(false);
    }
  }, [fieldMapping, currentStep, parseDataWithMapping]);

  // Когда заходим на шаг «Проверка данных», дополнительно гоняем серверную валидацию,
  // чтобы показать конфликты с уже существующими участниками/клубами.
  useEffect(() => {
    if (currentStep === 2 && parsedData.length > 0 && !serverValidationDone && !serverValidating) {
      runServerValidation();
    }
  }, [currentStep, parsedData, runServerValidation, serverValidationDone, serverValidating]);

  // Обработка загрузки файла
  const handleFileUpload = (file: File) => {
    parseFile(file);
    return false; // Предотвращаем автоматическую загрузку
  };

  // Редактирование строки
  const handleEditRow = (index: number) => {
    const participant = parsedData[index];
    // Сбрасываем старые значения формы, чтобы не тянуть поля
    // (например, фамилию) от предыдущего участника
    editForm.resetFields();
    editForm.setFieldsValue({
      firstName: participant.firstName || '',
      lastName: participant.lastName || '',
      middleName: participant.middleName || '',
      dateOfBirth: participant.dateOfBirth || undefined,
      gender: participant.gender || undefined,
      weight: participant.weight || undefined,
      beltLevel: participant.beltLevel || undefined,
      categoryName: participant.categoryName || undefined,
      federationName: participant.federationName || undefined,
      clubName: participant.clubName || undefined,
      region: participant.region || undefined,
      coachFullName: participant.coachFullName || undefined,
    });
    setEditingRow(index);
  };

  // Сохранение изменений в строке
  const handleSaveEdit = () => {
    editForm.validateFields().then(values => {
      // Тримминг всех строковых полей перед сохранением
      const trimmedValues: any = {};
      Object.entries(values).forEach(([key, value]) => {
        if (typeof value === 'string') {
          trimmedValues[key] = value.trim();
        } else {
          trimmedValues[key] = value;
        }
      });

      const updatedData = [...parsedData];
      updatedData[editingRow!] = { ...updatedData[editingRow!], ...trimmedValues };
      setParsedData(updatedData);
      validateData(updatedData);
      setEditingRow(null);
      message.success('Изменения сохранены');
    });
  };

  // Импорт данных
  const handleImport = async () => {
    const validData = parsedData.filter(p => p._valid);
    
    if (validData.length === 0) {
      message.warning('Нет валидных данных для импорта');
      return;
    }

    try {
      setImporting(true);

      // Преобразуем данные в формат API
      const participantsToImport = validData.map(p => {
        return {
          firstName: p.firstName,
          lastName: p.lastName,
          middleName: p.middleName || undefined,
          dateOfBirth: p.dateOfBirth ? parseDate(p.dateOfBirth.toString()) : undefined,
          gender: normalizeGender(p.gender?.toString()),
          weight: p.weight ? parseFloat(p.weight.toString()) : undefined,
          beltLevel: p.beltLevel || undefined,
          categoryName: p.categoryName?.toString().trim() || undefined,
          federationName: p.federationName?.toString().trim() || undefined,
          clubName: (() => {
            const v = p.clubName?.toString().trim();
            return v && !/^категори/i.test(v) ? v : undefined;
          })(),
          region: p.region?.toString().trim() || undefined,
          coachFullName: p.coachFullName?.toString().trim() || undefined,
        };
      });

      // Отправляем на сервер
      const response = await apiService.post<{ success: boolean; data: ImportResult; message?: string }>(
        `/participants/bulk-upload`,
        {
          participants: participantsToImport,
          tournamentId,
        }
      );

      if (response.data.success) {
        const result = response.data.data;
        setImportResult(result);
        setCurrentStep(3);

        if (result.failed === 0) {
          message.success(`Успешно импортировано ${result.success} участников`);
        } else {
          message.warning(`Импортировано ${result.success} участников, ${result.failed} с ошибками`);
        }
      } else {
        message.error('Ошибка при импорте участников');
      }
    } catch (error: any) {
      logger.error('Error importing participants:', error);
      const errorMessage = error.response?.data?.message || 'Ошибка при импорте участников';
      message.error(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  // Скачивание шаблона
  const handleDownloadTemplate = async () => {
    try {
      const response: AxiosResponse<Blob> = await apiService.get(
        `/tournaments/${tournamentId}/import-template`,
        { responseType: 'blob' }
      ) as AxiosResponse<Blob>;

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tournament_${tournamentId}_import_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('Шаблон успешно скачан');
    } catch (error) {
      logger.error('Error downloading template:', error);
      message.error('Ошибка при скачивании шаблона');
    }
  };

  // Вспомогательные функции
  const parseDate = (dateString: string): string | undefined => {
    if (!dateString || dateString.trim().length === 0) return undefined;

    const trimmed = dateString.trim();
    const ddmmyyyyMatch = trimmed.match(/^(\d{1,2})[.\/-](\d{1,2})[.\/-](\d{4})$/);
    if (ddmmyyyyMatch) {
      const [, day, month, year] = ddmmyyyyMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const date = new Date(trimmed);
    if (date instanceof Date && !isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return undefined;
  };

  const normalizeGender = (gender?: string): 'male' | 'female' | undefined => {
    if (!gender) return undefined;
    const g = gender.toLowerCase();
    if (g === 'male' || g === 'м' || g === 'мужской' || g === 'm') return 'male';
    if (g === 'female' || g === 'ж' || g === 'женский' || g === 'f') return 'female';
    return undefined;
  };

  // Сброс состояния
  const handleReset = () => {
    setCurrentStep(0);
    setFileList([]);
    setRawData([]);
    setHeaders([]);
    setFieldMapping({});
    setParsedData([]);
    setValidationErrors(new Map());
    setEditingRow(null);
    setImportResult(null);
  };

  // Колонки для таблицы маппинга
  const mappingColumns = [
    {
      title: 'Поле системы',
      dataIndex: 'field',
      key: 'field',
      width: 200,
      render: (field: string) => FIELD_LABELS[field] || field,
    },
    {
      title: 'Колонка в файле',
      dataIndex: 'column',
      key: 'column',
      render: (value: string | null, record: { field: string }) => (
        <Select
          style={{ width: '100%' }}
          value={value}
          placeholder="Выберите колонку"
          allowClear
          onChange={(val) => {
            setFieldMapping({ ...fieldMapping, [record.field]: val });
          }}
        >
          {headers.map(header => (
            <Select.Option key={header} value={header}>
              {header}
            </Select.Option>
          ))}
        </Select>
      ),
    },
  ];

  // Колонки для таблицы данных
  const dataColumns = [
    {
      title: 'Строка',
      dataIndex: '_rowIndex',
      key: '_rowIndex',
      width: 60,
    },
    {
      title: 'Фамилия',
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: 'Имя',
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: 'Отчество',
      dataIndex: 'middleName',
      key: 'middleName',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Категория',
      dataIndex: 'categoryName',
      key: 'categoryName',
      render: (value: any) => {
        return value || '-';
      },
    },
    {
      title: 'Федерация',
      dataIndex: 'federationName',
      key: 'federationName',
      render: (value: any) => {
        return value || '-';
      },
    },
    {
      title: 'Клуб',
      dataIndex: 'clubName',
      key: 'clubName',
      render: (value: any) => {
        const v = value != null ? String(value).trim() : '';
        // Не показывать заголовок колонки "Категория", попавший в поле клуба по ошибке маппинга
        if (!v || /^категори/i.test(v)) return null;
        return v;
      },
    },
    {
      title: 'Регион',
      dataIndex: 'region',
      key: 'region',
      render: (value: any) => {
        return value || '-';
      },
    },
    {
      title: 'Тренер',
      dataIndex: 'coachFullName',
      key: 'coachFullName',
      render: (value: any) => {
        return value || '-';
      },
    },
    {
      title: 'Статус',
      key: 'status',
      width: 120,
      render: (_: any, record: ParticipantData) => {
        const key = record._rowIndex ?? 0;
        const rowErrors =
          (record._errors && record._errors.length > 0 && record._errors) ||
          (key ? validationErrors.get(key) : undefined);

        if (rowErrors && rowErrors.length > 0) {
          return (
            <Tooltip title={rowErrors.join(', ')}>
              <Tag color="error" icon={<CloseCircleOutlined />}>
                Ошибки
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            OK
          </Tag>
        );
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_: any, record: ParticipantData) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => {
            const idx = parsedData.findIndex(
              (p) => p._rowIndex === record._rowIndex
            );
            if (idx !== -1) {
              handleEditRow(idx);
            }
          }}
        >
          Редактировать
        </Button>
      ),
    },
  ];

  const validCount = parsedData.filter(p => p._valid).length;
  const errorCount = parsedData.filter(p => !p._valid).length;

  return (
    <div>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={4}>Импорт участников</Title>
            <Paragraph>
              Загрузите файл Excel с данными участников для массового импорта в турнир.
              Обязательные поля: имя и фамилия. Клуб, федерация, тренер и др. — необязательны.
            </Paragraph>
          </div>

          <Steps current={currentStep}>
            <Step title="Загрузка файла" />
            <Step title="Маппинг полей" />
            <Step title="Проверка данных" />
            <Step title="Результат импорта" />
          </Steps>

          {currentStep === 0 && (
            <Card>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadTemplate}
                  >
                    Скачать шаблон Excel
                  </Button>
                </div>

                <div>
                  <Title level={5}>Загрузка файла</Title>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Upload
                      fileList={fileList}
                      beforeUpload={handleFileUpload}
                      onRemove={() => {
                        setFileList([]);
                        handleReset();
                      }}
                      accept=".xlsx,.xls"
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>Выбрать файл для пошагового импорта</Button>
                    </Upload>
                    <Divider>или</Divider>
                    <Upload
                      customRequest={async ({ file, onSuccess, onError }) => {
                        try {
                          setImporting(true);
                          const formData = new FormData();
                          formData.append('file', file as File);
                          formData.append('tournamentId', tournamentId.toString());

                          const response = await apiService.post<{ success: boolean; data: ImportResult; message?: string }>(
                            '/participants/bulk-upload/excel',
                            formData,
                            {
                              headers: {
                                'Content-Type': 'multipart/form-data',
                              },
                            }
                          );

                          if (response.data.success) {
                            const result = response.data.data;
                            setImportResult(result);
                            setCurrentStep(3);
                            onSuccess?.(response.data);
                            
                            if (result.failed === 0) {
                              message.success(`Успешно импортировано ${result.success} участников`);
                            } else {
                              message.warning(`Импортировано ${result.success} участников, ${result.failed} с ошибками`);
                            }
                          } else {
                            onError?.(new Error('Ошибка при импорте'));
                            message.error('Ошибка при импорте участников');
                          }
                        } catch (error: any) {
                          logger.error('Error uploading Excel file:', error);
                          const errorMessage = error.response?.data?.message || 'Ошибка при загрузке файла';
                          onError?.(error);
                          message.error(errorMessage);
                        } finally {
                          setImporting(false);
                        }
                      }}
                      accept=".xlsx,.xls"
                      maxCount={1}
                      showUploadList={false}
                    >
                      <Button 
                        icon={<UploadOutlined />} 
                        type="primary"
                        loading={importing}
                      >
                        Прямой импорт из Excel
                      </Button>
                    </Upload>
                  </Space>
                  <Paragraph type="secondary" style={{ marginTop: 8 }}>
                    Поддерживаются файлы Excel (.xlsx, .xls). Прямой импорт автоматически регистрирует участников в турнире.
                  </Paragraph>
                </div>
              </Space>
            </Card>
          )}

          {currentStep === 1 && (
            <Card>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                  <Row gutter={16}>
                    <Col span={20}>
                      <Title level={5}>Сопоставление полей</Title>
                    </Col>
                    <Col span={4} style={{ textAlign: 'right' }}>
                      <Checkbox
                        checked={autoMapEnabled}
                        onChange={(e) => {
                          setAutoMapEnabled(e.target.checked);
                          if (e.target.checked) {
                            const autoMapping = autoMapFields(headers);
                            setFieldMapping(autoMapping);
                          }
                        }}
                      >
                        Автоматический маппинг
                      </Checkbox>
                    </Col>
                  </Row>
                  <Paragraph type="secondary">
                    Сопоставьте колонки из файла с полями системы. Обязательные поля отмечены *
                  </Paragraph>
                </div>

                <Table
                  dataSource={[...REQUIRED_FIELDS, ...OPTIONAL_FIELDS].map(field => ({
                    key: field,
                    field,
                    column: fieldMapping[field] || null,
                  }))}
                  columns={mappingColumns}
                  pagination={false}
                  size="small"
                />

                <Alert
                  message="Обязательные поля"
                  description={
                    <div>
                      {REQUIRED_FIELDS.map(field => (
                        <Tag key={field} color="red" style={{ margin: '4px' }}>
                          {FIELD_LABELS[field]}
                        </Tag>
                      ))}
                    </div>
                  }
                  type="info"
                  showIcon
                />
                <Alert
                  message="Важно"
                  description="Если указана категория, участник будет автоматически зарегистрирован в турнир. Категория должна существовать в турнире."
                  type="warning"
                  showIcon
                  style={{ marginTop: 8 }}
                />
                {categories.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Title level={5}>Доступные категории:</Title>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {categories.map(cat => (
                        <Tag key={cat.id} color="blue">
                          {cat.name}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ textAlign: 'right' }}>
                  <Button onClick={() => setCurrentStep(0)} style={{ marginRight: 8 }}>
                    Назад
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => setCurrentStep(2)}
                    disabled={!fieldMapping.firstName || !fieldMapping.lastName}
                  >
                    Далее
                    <ArrowRightOutlined />
                  </Button>
                </div>
              </Space>
            </Card>
          )}

          {currentStep === 2 && (
            <Card>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {filteringExisting && (
                  <Alert
                    message="Исключаем уже зарегистрированных в турнире…"
                    description="Загружаем список участников турнира. В таблице останутся только новые из файла."
                    type="info"
                    showIcon
                  />
                )}
                {excludedExistingCount > 0 && !filteringExisting && (
                  <Alert
                    message={`В списке только новые участники`}
                    description={`Уже зарегистрированные в турнире (${excludedExistingCount}) исключены из списка и не будут показаны для проверки.`}
                    type="success"
                    showIcon
                  />
                )}
                <div>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic
                        title="Всего строк (только новые)"
                        value={parsedData.length}
                        prefix={<InfoCircleOutlined />}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Валидных"
                        value={validCount}
                        valueStyle={{ color: '#3f8600' }}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="С ошибками"
                        value={errorCount}
                        valueStyle={{ color: '#cf1322' }}
                        prefix={<CloseCircleOutlined />}
                      />
                    </Col>
                  </Row>
                </div>

                <Divider />

                <div>
                  <Title level={5}>Предпросмотр данных (только новые участники)</Title>
                  <Table
                    dataSource={parsedData}
                    columns={dataColumns}
                    rowKey={(record: ParticipantData) =>
                      record._rowIndex?.toString() ||
                      `${record.lastName || ''}-${record.firstName || ''}-${record.dateOfBirth || ''}`
                    }
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1400 }}
                    size="small"
                  />
                </div>

                {errorCount > 0 && (
                  <Alert
                    message={`Найдено ${errorCount} строк с ошибками`}
                    description="Исправьте ошибки перед импортом или отредактируйте строки вручную"
                    type="warning"
                    showIcon
                  />
                )}

                <div style={{ textAlign: 'right' }}>
                  <Button onClick={() => setCurrentStep(1)} style={{ marginRight: 8 }}>
                    Назад
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleImport}
                    loading={importing}
                    disabled={validCount === 0}
                  >
                    Импортировать ({validCount})
                  </Button>
                </div>
              </Space>
            </Card>
          )}

          {currentStep === 3 && importResult && (
            <Card>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Alert
                  message={`Импорт завершен: ${importResult.success} успешно, ${importResult.failed} с ошибками`}
                  type={importResult.failed === 0 ? 'success' : 'warning'}
                  showIcon
                />

                {importResult.errors && importResult.errors.length > 0 && (
                  <div>
                    <Title level={5}>Ошибки при импорте:</Title>
                    <Table
                      dataSource={importResult.errors}
                      rowKey={(record: ImportError) => `error-${record.row}-${record.error}`}
                      columns={[
                        { title: 'Строка', dataIndex: 'row', key: 'row', width: 80 },
                        { title: 'Ошибка', dataIndex: 'error', key: 'error' },
                      ]}
                      pagination={{ pageSize: 10 }}
                      size="small"
                    />
                  </div>
                )}

                {importResult.suggestions && importResult.suggestions.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Title level={5}>Предложенные категории:</Title>
                    <Alert
                      message="Для некоторых участников категория не была найдена, но была предложена наиболее подходящая по параметрам"
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Table
                      dataSource={importResult.suggestions}
                      rowKey={(record) =>
                        `suggestion-${record.row}-${record.originalCategory}-${record.suggestedCategory?.id ?? ''}`
                      }
                      columns={[
                        { title: 'Строка', dataIndex: 'row', key: 'row', width: 80 },
                        { 
                          title: 'Исходная категория', 
                          dataIndex: 'originalCategory', 
                          key: 'originalCategory' 
                        },
                        { 
                          title: 'Предложенная категория', 
                          dataIndex: ['suggestedCategory', 'name'], 
                          key: 'suggestedCategory',
                          render: (value: string) => (
                            <Tag color="blue">{value}</Tag>
                          )
                        },
                      ]}
                      pagination={{ pageSize: 10 }}
                      size="small"
                    />
                  </div>
                )}

                {importResult.created && importResult.created.length > 0 && (
                  <div>
                    <Title level={5}>Успешно импортировано:</Title>
                    <Table
                      dataSource={importResult.created}
                      rowKey={(record) =>
                        record.userId?.toString() ||
                        `${record.firstName || ''}-${record.lastName || ''}-${record.email || ''}`
                      }
                      columns={[
                        { title: 'Имя', dataIndex: 'firstName', key: 'firstName' },
                        { title: 'Фамилия', dataIndex: 'lastName', key: 'lastName' },
                      ]}
                      pagination={{ pageSize: 10 }}
                      size="small"
                    />
                  </div>
                )}

                <div style={{ textAlign: 'right' }}>
                  <Button type="primary" onClick={handleReset}>
                    Импортировать еще
                  </Button>
                </div>
              </Space>
            </Card>
          )}
        </Space>
      </Card>

      {/* Модальное окно редактирования строки */}
      <Modal
        title="Редактирование участника"
        open={editingRow !== null}
        onOk={handleSaveEdit}
        onCancel={() => setEditingRow(null)}
        width={600}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="firstName"
            label="Имя *"
            rules={[{ required: true, message: 'Имя обязательно' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Фамилия *"
            rules={[{ required: true, message: 'Фамилия обязательна' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="middleName" label="Отчество">
            <Input />
          </Form.Item>
          <Form.Item name="dateOfBirth" label="Дата рождения (ДД.ММ.ГГГГ)">
            <Input placeholder="15.01.2000" />
          </Form.Item>
          <Form.Item name="gender" label="Пол">
            <Select placeholder="Выберите пол">
              <Select.Option value="male">Мужской</Select.Option>
              <Select.Option value="female">Женский</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="weight" label="Вес (кг)">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="beltLevel" label="Уровень пояса">
            <Input placeholder="11_kyu, 10_kyu, 1_kyu, 1_dan и т.д." />
          </Form.Item>
          <Form.Item name="categoryName" label="Категория">
            <Select placeholder="Выберите категорию" allowClear showSearch>
              {categories.map(cat => (
                <Select.Option key={cat.id} value={cat.name}>
                  {cat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="federationName" label="Название федерации">
            <Input placeholder="Федерация каратэ России" />
          </Form.Item>
          <Form.Item name="clubName" label="Название клуба (необязательно)">
            <Input placeholder="Клуб &quot;Спорт&quot; или оставьте пустым" />
          </Form.Item>
          <Form.Item name="region" label="Регион">
            <Input placeholder="Москва" />
          </Form.Item>
          <Form.Item name="coachFullName" label="Тренер (ФИО)">
            <Input placeholder="Петров Петр Петрович" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
